// src/pages/Servicios/ServiciosAbonados.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { fetchServices } from "../../services/serviceService";
import { useRef } from "react";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BalanceIcon from "@mui/icons-material/Balance";

/* ===== Paletas coherentes ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/* ===== Widget ===== */
function StatCard({ icon, label, value, palette, highlight = false, isMobile = false }) {
  return (
    <Box
      sx={{
        p: isMobile ? 1.75 : 2,
        borderRadius: "12px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? 1 : 1.25,
        minHeight: 76,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2,6,23,0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Box sx={{ opacity: 0.95, display: "grid", placeItems: "center" }}>{icon}</Box>
      <Box sx={{ flex: 1, textAlign: isMobile ? "center" : "left" }}>
        <Typography
          variant="caption"
          sx={{
            color: palette.label,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ===== Card contenedora ===== */
function Card({ title, palette, children, sx: sxProp = {}, action = null, isMobile = false }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 1.5,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2,6,23,0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
        overflow: "hidden",
        ...sxProp,
      }}
    >
      {isMobile ? (
        <>
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
              {title}
            </Typography>
          </Box>
          {action && (
            <Box sx={{ mb: 1, display: "flex", flexDirection: "column", gap: 1 }}>
              {action}
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: action ? "space-between" : "flex-start",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
            {title}
          </Typography>
          {action && <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{action}</Box>}
        </Box>
      )}
      {/* 👇 ESTE contenedor ahora da altura real al DataGrid */}
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
        {children}
      </Box>
    </Box>
  );
}

/* ===== Estilo DataGrid (el wrapper y el root con 100% de alto) ===== */
const dgSx = (palette) => ({
  height: "100%",               // 👈 el wrapper del grid ocupa todo
  width: "100%",
  display: "flex",
  "& .MuiDataGrid-root": {
    border: "none",
    color: palette.text,
    backgroundColor: "transparent",
    height: "100%",             // 👈 el propio root ocupa todo
    width: "100%",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "transparent",
    borderBottom: `1px solid ${palette.cardBorder}`,
    color: palette.label,
    fontWeight: 700,
  },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${palette.cardBorder}`,
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-overlay": {
    backgroundColor: "transparent",
    color: palette.label,
  },
});

/* ===== Utils ===== */
function parseDateES(str) {
  if (!str) return null;
  if (/^\d{2}\/\d{2}\/\d{4}/.test(str)) {
    // admite "DD/MM/YY" o "DD/MM/YYYY HH:mm"
    const [fecha] = str.split(" ");
    const [d, m, yRaw] = fecha.split("/");
    const y = yRaw.length === 2 ? Number(`20${yRaw}`) : Number(yRaw);
    return new Date(y, Number(m) - 1, Number(d));
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}
const eurToNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  return Number(String(v).replace(/\./g, "").replace(",", "."));
};

const mapService = (svc) => ({
  SERVICIO: svc._id || svc.externalId,
  "FECHA INICIO": svc.fechaInicio ? new Date(svc.fechaInicio).toLocaleString() : "",
  "FECHA FIN": svc.fechaFin ? new Date(svc.fechaFin).toLocaleString() : "",
  "MÉTODO DE PAGO": svc.metodoPago,
  "TIPO DE SERVICIO": svc.tipoServicio,
  "DISTANCIA km.": svc.distanciaKm,
  "DIRECCIÓN INICIO": svc.direccionInicio,
  "DIRECCIÓN FIN": svc.direccionFin,
  "TOTAL EUR": svc.importes?.total ?? svc.importeTotal ?? 0,
});

const ServiciosAbonados = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const isMobile = useMediaQuery("(max-width:900px)");

  const [abonadosBase, setAbonadosBase] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const desdeRef = useRef(null);
  const hastaRef = useRef(null);

  // Rango de fechas (último mes por defecto)
  const formatLocalDate = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const getInicioMes = () => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const getHoy = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const [desde, setDesde] = useState(formatLocalDate(getInicioMes()));
  const [hasta, setHasta] = useState(formatLocalDate(getHoy()));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchServices({
          paymentMethod: "Abonado",
          startDate: desde,
          endDate: hasta,
        });
        setAbonadosBase(data.map(mapService));
      } catch (e) {
        setError(e.message || "No se pudieron cargar los servicios");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [desde, hasta]);

  const abonados = useMemo(() => {
    const dMin = desde ? new Date(desde) : null;
    const dMax = hasta ? new Date(hasta) : null;
    if (!dMin && !dMax) return abonadosBase;

    return abonadosBase.filter((row) => {
      const f = parseDateES(row["FECHA INICIO"]);
      if (!f) return false;
      if (dMin && f < dMin) return false;
      if (dMax) {
        const end = new Date(dMax);
        end.setHours(23, 59, 59, 999);
        if (f > end) return false;
      }
      return true;
    });
  }, [abonadosBase, desde, hasta]);

  // KPIs
  const totalServicios = abonados.length;
  const totalImporteNum = abonados.reduce(
    (sum, s) => sum + eurToNumber(s["TOTAL EUR"]),
    0
  );
  const totalImporte = totalImporteNum.toFixed(2);
  const importeMedio =
    totalServicios > 0 ? (totalImporteNum / totalServicios).toFixed(2) : "0.00";

  // Columnas
  const columns = [
    { field: "SERVICIO", headerName: "ID", flex: 1 },
    { field: "FECHA INICIO", headerName: "Fecha Inicio", flex: 1 },
    { field: "FECHA FIN", headerName: "Fecha Fin", flex: 1 },
    {
      field: "TOTAL EUR",
      headerName: "Importe (€)",
      flex: 1,
      valueGetter: (params) => eurToNumber(params.value).toFixed(2),
    },
    { field: "DIRECCIÓN INICIO", headerName: "Origen", flex: 2 },
    { field: "DIRECCIÓN FIN", headerName: "Destino", flex: 2 },
    {
      field: "accion",
      headerName: "Acción",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/servicio/${params.row.SERVICIO}`)}
          sx={{
            backgroundColor: palette.btn,
            color: "white",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: palette.btnHover },
          }}
        >
          Ver detalle
        </Button>
      ),
    },
  ];

  const dateFilters = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
      gap={1}
      sx={{ width: "100%", flexWrap: "wrap" }}
    >
      <TextField
        type="date"
        size="small"
        value={desde}
        onChange={(e) => setDesde(e.target.value)}
        label="Desde"
        InputLabelProps={{ shrink: true }}
        inputRef={desdeRef}
        onClick={() => {
          if (desdeRef.current?.showPicker) desdeRef.current.showPicker();
        }}
        sx={{
          minWidth: { xs: "100%", sm: 150 },
          "& .MuiInputLabel-root": { color: palette.label },
          "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
          "& .MuiInputBase-input": { color: palette.text },
          "& .MuiInputBase-input.Mui-focused": { color: palette.text },
          "& .MuiSvgIcon-root": { color: palette.text },
          "& input::-webkit-calendar-picker-indicator": {
            filter: isDark ? "invert(1)" : "invert(0)",
          },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.cardBorder },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: palette.label },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.accent,
          },
        }}
      />
      <TextField
        type="date"
        size="small"
        value={hasta}
        onChange={(e) => setHasta(e.target.value)}
        label="Hasta"
        InputLabelProps={{ shrink: true }}
        inputRef={hastaRef}
        onClick={() => {
          if (hastaRef.current?.showPicker) hastaRef.current.showPicker();
        }}
        sx={{
          minWidth: { xs: "100%", sm: 150 },
          "& .MuiInputLabel-root": { color: palette.label },
          "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
          "& .MuiInputBase-input": { color: palette.text },
          "& .MuiInputBase-input.Mui-focused": { color: palette.text },
          "& .MuiSvgIcon-root": { color: palette.text },
          "& input::-webkit-calendar-picker-indicator": {
            filter: isDark ? "invert(1)" : "invert(0)",
          },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.cardBorder },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: palette.label },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.accent,
          },
        }}
      />
      <Button
        onClick={() => {
          setDesde(formatLocalDate(getInicioMes()));
          setHasta(formatLocalDate(getHoy()));
        }}
        variant="outlined"
        sx={{
          borderColor: palette.cardBorder,
          color: palette.text,
          textTransform: "none",
          fontWeight: 600,
          "&:hover": { borderColor: palette.accent },
          whiteSpace: "nowrap",
          alignSelf: { xs: "stretch", sm: "center" },
        }}
      >
        Reiniciar
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ m: "20px", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Header title="Servicios de Abonado" subtitle="Listado de servicios donde el pago ha sido abonado" />
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* Widgets */}
      <Box
        sx={{
          mt: 1,
          mb: 1,
          display: "flex",
          gap: 2,
          width: "100%",
          justifyContent: "space-between",
          flexWrap: "nowrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<DescriptionIcon />}
            label="Total servicios"
            value={totalServicios}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<AttachMoneyIcon />}
            label="Importe total"
            value={`€${totalImporte}`}
            palette={palette}
            highlight
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<BalanceIcon />}
            label="Importe medio"
            value={`€${importeMedio}`}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
      </Box>

      {/* Filtros + grid */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          gap: { xs: 1.5, md: 2 },
        }}
      >
        {!isMobile && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", pt: { md: 0.5 } }}>
            {dateFilters}
          </Box>
        )}
        <Card
          title="Servicios abonados"
          palette={palette}
          isMobile={isMobile}
          action={isMobile ? dateFilters : null}
        >
          {/* 👇 ESTE Box es el contenedor con altura real */}
          <Box sx={dgSx(palette)}>
            {loading ? (
              <Typography sx={{ color: palette.label, p: 2 }}>Cargando servicios...</Typography>
            ) : (
              <DataGrid
                rows={abonados}
                columns={columns}
                getRowId={(row) => row.SERVICIO}
                disableRowSelectionOnClick
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                pageSizeOptions={[5, 10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                rowHeight={52}
                columnHeaderHeight={48}
                columnVisibilityModel={
                  isMobile
                    ? {
                        SERVICIO: false,
                        "FECHA INICIO": false,
                        "DIRECCIÓN INICIO": false,
                        "DIRECCIÓN FIN": false,
                      }
                    : undefined
                }
                sx={{ height: "100%", width: "100%" }}   // 👈 el grid ocupa el 100%
              />
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default ServiciosAbonados;
